import { useEffect, useState } from 'react';
import { schedulerAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Calendar } from '../components/ui/calendar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, isBefore, startOfDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import {
  Calendar as CalendarIcon, Clock, User, Video, Check, ChevronRight, ChevronLeft, RefreshCw,
  Phone, MessageCircle
} from 'lucide-react';

/**
 * CustomerPortalBookMeetingPage - Embedded meeting scheduler for customer portal
 * Pre-fills customer details from logged-in user
 */
const CustomerPortalBookMeetingPage = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(null);

  // Form state - pre-fill with customer details
  const [selectedType, setSelectedType] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [meetingMethod, setMeetingMethod] = useState('zoom');

  useEffect(() => {
    fetchMeetingTypes();
    // Pre-fill customer details
    if (user) {
      setCustomerName(`${user.first_name || ''} ${user.last_name || ''}`.trim());
      setCustomerEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    if (selectedType) {
      fetchAgents();
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedAgent && selectedType && selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedAgent, selectedType, selectedDate]);

  const fetchMeetingTypes = async () => {
    try {
      const res = await schedulerAPI.getMeetingTypes();
      setMeetingTypes(res.data);
      // Auto-select if only one type
      if (res.data.length === 1) {
        setSelectedType(res.data[0]);
        setStep(2);
      }
    } catch (error) {
      toast.error('Failed to load meeting types');
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await schedulerAPI.getAgents(selectedType?.id);
      setAgents(res.data);
      // Auto-select if only one agent
      if (res.data.length === 1) {
        setSelectedAgent(res.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load available agents');
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedAgent || !selectedType || !selectedDate) return;
    setLoadingSlots(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await schedulerAPI.getAvailableSlots(selectedAgent.id, selectedType.id, dateStr);
      setAvailableSlots(res.data);
    } catch (error) {
      toast.error('Failed to load available slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async () => {
    if (!customerName || !customerEmail) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await schedulerAPI.bookMeeting({
        customer_name: customerName,
        customer_email: customerEmail,
        agent_id: selectedAgent.id,
        meeting_type_id: selectedType.id,
        scheduled_datetime: selectedSlot.datetime_iso,
        meeting_method: meetingMethod,
        notes: notes || undefined
      });
      setBookingComplete(res.data);
      toast.success('Meeting booked successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book meeting');
    } finally {
      setLoading(false);
    }
  };

  const getAgentInitials = (agent) => {
    if (agent.first_name && agent.last_name) {
      return `${agent.first_name[0]}${agent.last_name[0]}`;
    }
    if (agent.full_name) {
      const parts = agent.full_name.split(' ');
      return parts.map(p => p[0]).join('').substring(0, 2);
    }
    return agent.email?.[0]?.toUpperCase() || 'A';
  };

  const getAgentName = (agent) => {
    return agent.full_name || `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.email;
  };

  const resetBooking = () => {
    setBookingComplete(null);
    setStep(1);
    setSelectedType(null);
    setSelectedAgent(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setNotes('');
    setMeetingMethod('zoom');
  };

  // Success screen
  if (bookingComplete) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="booking-complete">
        <Card className="max-w-lg mx-auto">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Meeting Booked!</h2>
            <p className="text-muted-foreground mb-6">
              A confirmation email will be sent to {bookingComplete.customer_email}
            </p>
            
            <div className="bg-slate-50 rounded-lg p-4 text-left space-y-3">
              <div className="flex items-center gap-3">
                <Video className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{bookingComplete.meeting_type}</span>
              </div>
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                <span>{format(new Date(bookingComplete.scheduled_datetime), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span>{format(new Date(bookingComplete.scheduled_datetime), 'h:mm a')} ({bookingComplete.duration_minutes} min)</span>
              </div>
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <span>{bookingComplete.agent_name}</span>
              </div>
            </div>
            
            <Button className="mt-6 w-full" onClick={resetBooking}>
              Book Another Meeting
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-portal-book-meeting">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Book a Meeting</h1>
        <p className="text-muted-foreground">Schedule time with our support team</p>
      </div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Video className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Video Call</h3>
                <p className="text-sm text-blue-700">Virtual meetings available</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Phone className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900">Phone Calls</h3>
                <p className="text-sm text-emerald-700">We can call you directly</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <MessageCircle className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900">Consultation</h3>
                <p className="text-sm text-purple-700">Expert guidance available</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center">
        {[
          { num: 1, label: 'Meeting Type' },
          { num: 2, label: 'Agent & Time' },
          { num: 3, label: 'Confirm' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step > s.num ? 'bg-emerald-500 text-white' :
                step === s.num ? 'bg-blue-600 text-white' : 'bg-slate-200'
              }`}>
                {step > s.num ? <Check className="h-4 w-4" /> : s.num}
              </div>
              <span className="hidden sm:block font-medium">{s.label}</span>
            </div>
            {i < 2 && <ChevronRight className="h-5 w-5 mx-4 text-slate-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Select Meeting Type */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Select Meeting Type</CardTitle>
            <CardDescription>Choose the type of meeting you&apos;d like to schedule</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {meetingTypes.map(type => (
                <div
                  key={type.id}
                  onClick={() => {
                    setSelectedType(type);
                    setStep(2);
                  }}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-blue-400 ${
                    selectedType?.id === type.id ? 'border-blue-600 bg-blue-50' : 'border-slate-200'
                  }`}
                  data-testid={`meeting-type-${type.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge className={type.color || 'bg-blue-100 text-blue-700'}>{type.category}</Badge>
                      <h3 className="font-semibold mt-2">{type.name}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{type.duration_minutes} minutes</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </div>
              ))}
              {meetingTypes.length === 0 && (
                <div className="col-span-2 text-center py-12 text-muted-foreground">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No meeting types available</p>
                  <p className="text-sm">Please contact support to schedule a meeting.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select Agent & Time */}
      {step === 2 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Select Agent & Time</CardTitle>
                  <CardDescription>Choose who you&apos;d like to meet with and when</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Agent Selection */}
                <div className="space-y-4">
                  <Label>Select Agent</Label>
                  <div className="space-y-2">
                    {agents.map(agent => (
                      <div
                        key={agent.id}
                        onClick={() => {
                          setSelectedAgent(agent);
                          setSelectedSlot(null);
                        }}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedAgent?.id === agent.id ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`agent-${agent.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-slate-200">
                              {getAgentInitials(agent)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{getAgentName(agent)}</p>
                            <p className="text-xs text-muted-foreground capitalize">{agent.role?.replace('_', ' ')}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {agents.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No agents available for this meeting type</p>
                    )}
                  </div>
                </div>

                {/* Calendar */}
                <div className="space-y-4">
                  <Label>Select Date</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setSelectedSlot(null);
                    }}
                    disabled={(date) => isBefore(date, startOfDay(new Date()))}
                    className="rounded-md border"
                  />
                </div>

                {/* Time Slots */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Select Time</Label>
                    {availableSlots.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        Mountain Standard Time
                      </Badge>
                    )}
                  </div>
                  {!selectedAgent && (
                    <p className="text-sm text-muted-foreground py-4">Please select an agent first</p>
                  )}
                  {!selectedDate && selectedAgent && (
                    <p className="text-sm text-muted-foreground py-4">Please select a date</p>
                  )}
                  {loadingSlots && (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                  )}
                  {selectedAgent && selectedDate && !loadingSlots && (
                    <>
                      <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                        {availableSlots.map((slot, i) => (
                          <Button
                            key={i}
                            variant={selectedSlot?.start_time === slot.start_time ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedSlot(slot)}
                            data-testid={`slot-${slot.start_time}`}
                          >
                            {slot.start_time}
                          </Button>
                        ))}
                        {availableSlots.length === 0 && (
                          <p className="col-span-2 text-sm text-muted-foreground text-center py-4">
                            No slots available for this date
                          </p>
                        )}
                      </div>
                      {availableSlots.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          All times shown in Mountain Standard Time (MST)
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button 
              onClick={() => setStep(3)} 
              disabled={!selectedAgent || !selectedDate || !selectedSlot}
              data-testid="continue-to-details"
            >
              Continue <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm Details */}
      {step === 3 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Confirm Your Details</CardTitle>
                  <CardDescription>Review and confirm your meeting details</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setStep(2)}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="John Doe"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="john@example.com"
                      data-testid="customer-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="meeting-method">How would you like to connect? *</Label>
                    <Select value={meetingMethod} onValueChange={setMeetingMethod}>
                      <SelectTrigger id="meeting-method" data-testid="meeting-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="zoom">Zoom video call</SelectItem>
                        <SelectItem value="phone">Phone call</SelectItem>
                        <SelectItem value="other">Other (specify in notes)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Our team will email you the {meetingMethod === 'zoom' ? 'Zoom link' : meetingMethod === 'phone' ? 'call details' : 'connection details'} before your meeting.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything specific you'd like to discuss?"
                      rows={3}
                      data-testid="notes-input"
                    />
                  </div>
                </div>

                {/* Booking Summary */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-semibold mb-4">Booking Summary</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Video className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium">{selectedType?.name}</p>
                        <p className="text-sm text-muted-foreground">{selectedType?.duration_minutes} minutes</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium">{getAgentName(selectedAgent)}</p>
                        <p className="text-sm text-muted-foreground capitalize">{selectedAgent?.role?.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CalendarIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium">{selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                        <p className="text-sm text-muted-foreground">{selectedSlot?.start_time} - {selectedSlot?.end_time}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !customerName || !customerEmail}
              data-testid="confirm-booking-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Confirm Booking
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPortalBookMeetingPage;
